/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormLabel } from '@/components/ui/form';
import { useTranslation } from 'react-i18next';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar } from '@/components/ui/calendar';
import { getMonthNameByDate } from '@/utils/dateFunctions/getDatesOfMonth';
import { YearMonthDrawerMobile } from '@/domain/termine/components/YearMonthPicker/mobile/YearMonthDrawerMobile';
import { useDatePicker } from '../../shared/hooks/useDatePicker';

interface DatePickerDrawerProps {
  value: Dayjs | undefined;
  onChange: (value: Dayjs | undefined) => void;
  label: string;
  placeholder?: string;
  className?: string;
}

export function DatePickerDrawer({
  value,
  onChange,
  label,
  placeholder,
  className,
}: DatePickerDrawerProps) {
  const { t } = useTranslation();
  const {
    isOpen,
    tempDate,
    displayMonth,
    handleOpenChange,
    handleConfirm,
    handleDateSelect,
    handleYearMonthSelect,
    handlePreviousMonth,
    handleNextMonth,
    handleTodaySelect,
  } = useDatePicker({ value, onChange });

  const selectedDate = value?.toDate();
  const displayValue = useMemo(
    () =>
      selectedDate
        ? dayjs(selectedDate).locale('de').format('dd., D. MMMM YYYY')
        : placeholder ?? label,
    [selectedDate, placeholder, label],
  );

  const displayMonthYear = useMemo(
    () => ({
      year: dayjs(displayMonth).format('YYYY'),
      month: dayjs(displayMonth).format('M'),
      formatted: dayjs(displayMonth).format('YYYYM'),
    }),
    [displayMonth],
  );

  return (
    <>
      <FormLabel>{label}</FormLabel>
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          <Input
            endIcon={faCalendar}
            className={`cursor-pointer text-left ${
              !selectedDate ? className : ''
            }`}
            value={displayValue}
            readOnly
          />
        </DrawerTrigger>
        <DrawerContent>
          <div className="w-full p-4" data-vaul-no-drag>
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePreviousMonth}
                className="h-7 w-7">
                <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
              </Button>

              <YearMonthDrawerMobile
                currentMonthAndYear={{
                  year: displayMonthYear.year,
                  month: displayMonthYear.month,
                }}
                onSelect={handleYearMonthSelect}
                triggerClassName="hover:bg-neutrals-N100">
                <Button variant="ghost" className="hover:bg-neutrals-N100">
                  <span className="font-semibold text-sm">
                    {getMonthNameByDate(displayMonthYear.formatted)}
                  </span>
                </Button>
              </YearMonthDrawerMobile>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextMonth}
                className="h-7 w-7">
                <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
              </Button>
            </div>

            <Calendar
              mode="single"
              selected={tempDate}
              onSelect={handleDateSelect}
              month={displayMonth}
              classNames={{
                months: 'flex flex-col space-y-4',
                month: 'space-y-4',
                caption: 'hidden',
                nav: 'hidden',
                head_cell:
                  'text-slate-500 h-9 w-full font-normal text-[0.8rem]',
                cell: 'h-9 w-full text-center text-sm p-0 relative',
                day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md',
                day_today:
                  'bg-slate-200 text-slate-900 aria-selected:bg-primary-blue-B600 aria-selected:text-white',
                day_selected:
                  'bg-primary-blue-B600 text-white hover:bg-primary-blue-B600 hover:text-white focus:bg-primary-blue-B600 focus:text-white',
              }}
            />
          </div>
          <div className="px-4 pb-4 flex flex-col gap-3">
            <Button
              className="w-full"
              variant="ghost"
              onClick={handleTodaySelect}>
              {t('general.date.today')}
            </Button>
            <DrawerClose asChild>
              <Button className="w-full" size="lg" onClick={handleConfirm}>
                {t('domain.termine.form.fertig')}
              </Button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
